import React, { useEffect, useMemo, useState } from 'react';
import {
    Button,
    Empty,
    Input,
    message,
    Modal,
    Select,
    Space,
    Table,
    Tooltip,
    Card,
    Spin, Checkbox,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
    FilePdfOutlined,
    ReloadOutlined,
    AppstoreOutlined,
    PlusSquareOutlined,
    MinusSquareOutlined, CalendarOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { getConfig } from '../../../config';
import { useProjectContext } from '../../../context/ProjectContext';
import './OrderOverviewConfiguration.css';

const { API_BASE_URL, X_API_KEY } = getConfig();

// ---- API Types --------------------------------------------------------------

interface StatusMapping {
    statusNo: number;
    statusName: string;
    statusColor: string;
    statusNameEnglish?: string;
}

interface OrderAPI {
    id: number;
    supplierNo: string | null;
    status: number | null;
    purchaseOrderDate: string | null;
    projectNo: string;
    protocol: number;
    supplement: number;
    purchaseOrderNo: string;
    purchaseOrderIdentifierNo: string;
    ourReference: string | null;
    deliveryDateMax: string | null;
    deliveryDateConfirmedMax: string | null;
    goodsReceivingDate: string | null;
    weight: string | null;
    totalSum: string | null;
    currency: string | null;
    statusName: string | null;
    statusColor: string | null;
    statusMapping?: StatusMapping;
    fullSupplierName: string | null;
}

/** Felder aus getNetworkPlanOrderCardData */
interface OrderCardAPI {
    projectNo: string;
    supplement: number;
    protocol: number;
    description?: string;
    mountingDate?: string;
    statusName?: string;
    statusColor?: string;
    status?: string;
}

type Row = {
    key: string;
    supplierNo: string | null;
    statusName: string;
    statusColor: string;
    purchaseOrderIdentifierNo: string;
    fullSupplierName: string;
    ourReference: string;
    purchaseOrderDate: string;
    deliveryDateMax: string;
    deliveryDateConfirmedMax: string;
    goodsReceivingDate: string;
    projectNo: string;
    supplement: number;
    protocol: number;
    weight: string;
    totalSum: string;
    currency: string;
};

// ---- Utils ------------------------------------------------------------------

const pad2 = (n: number) => String(n).padStart(2, '0');
const composeProjectSp = (projectNo: string, supp: number, prot: number) =>
    `${projectNo}/${pad2(supp)}/${pad2(prot)}`;

const Badge = ({ text, color }: { text?: string; color?: string }) => (
    <span className="oo2-badge" style={{ backgroundColor: color || '#e7e7e7' }}>
    {text || '—'}
  </span>
);

const fmt2 = (value?: string | number | null) => {
    const num = typeof value === 'string' ? Number(value.replace(',', '.')) : Number(value);
    if (Number.isFinite(num)) {
        return num.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return '—';
};

// Parser: "dd.MM.yyyy" oder "yyyy-MM-dd"
const parseDateLoose = (s?: string | null): Date | null => {
    if (!s) return null;
    const t = s.trim();
    const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(t);
    if (m) return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
    const n = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
    if (n) return new Date(Number(n[1]), Number(n[2]) - 1, Number(n[3]));
    const d = new Date(t);
    return isNaN(d.getTime()) ? null : d;
};

const fmtDe = (d: Date) =>
    `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;

const getLatestDeliveryText = (items: Row[]) => {
    // zuerst bestätigte, sonst gewünschte
    const dates: Date[] = [];
    for (const it of items) {
        const d1 = parseDateLoose(it.deliveryDateConfirmedMax);
        const d2 = parseDateLoose(it.deliveryDateMax);
        if (d1) dates.push(d1);
        else if (d2) dates.push(d2);
    }
    if (!dates.length) return '—';
    const latest = dates.reduce((a, b) => (a > b ? a : b));
    return fmtDe(latest);
};

// Auftragskarten-Status einfärben: blau bei "Montiert", sonst orange
const getOrderCardStatusColored = (card?: OrderCardAPI) => {
    const raw = (card?.statusName || (card as any)?.status || '').trim().toLowerCase();
    if (!raw) return { text: '—', color: '#e7e7e7' };
    const isMounted = /montiert/.test(raw);
    return {
        text: card?.statusName || (card as any)?.status || '—',
        color: isMounted ? 'blue' : '#faad14',
    };
};

// --- Status-Helfer -----------------------------------------------------------
const norm = (s?: string | null) => (s ?? '').trim().toLowerCase();
const isCancelled = (r: Row) => norm(r.statusName).includes('storniert');
// direkt unter getOrderCardStatusColored(...)
const isCardMounted = (card?: OrderCardAPI) => {
    const raw = (card?.statusName || (card as any)?.status || '').trim().toLowerCase();
    return /montiert/.test(raw);
};

// ISO-Woche -> Montag der KW als Date
const isoWeekToDate = (year: number, week: number): Date => {
    // Donnerstag der Woche 1
    const jan4 = new Date(year, 0, 4);
    const dayOfWeek = (jan4.getDay() + 6) % 7; // Mo=0..So=6
    const mondayWeek1 = new Date(jan4);
    mondayWeek1.setDate(jan4.getDate() - dayOfWeek);
    const mondayTarget = new Date(mondayWeek1);
    mondayTarget.setDate(mondayWeek1.getDate() + (week - 1) * 7);
    return mondayTarget;
};

// Montagedatum kann "dd.MM.yyyy", "yyyy-MM-dd" oder "yyyy/WW" sein
const parseMountingDate = (s?: string | null): Date | null => {
    if (!s) return null;
    const t = s.trim();
    const mKw = /^(\d{4})[\/-](\d{1,2})$/.exec(t); // "2025/49"
    if (mKw) {
        const y = Number(mKw[1]);
        const w = Number(mKw[2]);
        if (y > 1900 && w >= 1 && w <= 53) return isoWeekToDate(y, w);
    }
    return parseDateLoose(t);
};

// Für Sortierung: fehlende Daten ans Ende
const mountSortKey = (card?: OrderCardAPI): number => {
    const raw = card?.mountingDate ?? (card as any)?.MountingDate ?? '';
    const d = parseMountingDate(raw);
    return d ? d.getTime() : Number.POSITIVE_INFINITY;
};

// Robust: exakt 2585 als Lieferantennummer auswerten
const isMontageMuessig = (r: Row) => (r.supplierNo ?? '').trim() === '2585';

// Wareneingang nur anhand des Status-Texts bewerten
const hasGoodsReceiptByStatus = (r: Row) => norm(r.statusName).includes('wareneingang');

// Aggregierter Bestellstatus (Montage & Storno ignorieren)
const getAggregatedOrderStatus = (items: Row[]) => {
    // 1) Irrelevante Positionen raus
    const relevant = items.filter(i => !isCancelled(i) && !isMontageMuessig(i));

    // 2) Wenn es keine relevanten Positionen gibt: alles geliefert
    if (relevant.length === 0) {
        return { text: 'Vollständig geliefert', color: 'blue' };
    }

    // 3) Nur relevante Positionen betrachten
    const allReceived = relevant.every(hasGoodsReceiptByStatus);
    return allReceived
        ? { text: 'Vollständig geliefert', color: 'blue' }
        : { text: 'Noch in Produktion', color: '#faad14' };
};


// -----------------------------------------------------------------------------

const OrderOverviewConfiguration: React.FC = () => {
    const { selectedProject } = useProjectContext();

    const [rows, setRows] = useState<Row[]>([]);
    const [loading, setLoading] = useState(false);
    const [grouped, setGrouped] = useState(true);
    const [onlyInProduction, setOnlyInProduction] = useState(false);
    const [onlyNotMounted, setOnlyNotMounted] = useState(false);
    type MountSort = 'none' | 'asc' | 'desc';
    const [mountSort, setMountSort] = useState<MountSort>('none');

    // Auf-/Zuklapp-Zustand: key = "supp-prot"
    const [expandedMap, setExpandedMap] = useState<Record<string, boolean>>({});

    // OrderCards Map: key "supp-prot" -> Daten
    const [cardMap, setCardMap] = useState<Map<string, OrderCardAPI>>(new Map());

    // Header-Filter
    const [fStatus, setFStatus] = useState<string | undefined>(undefined);
    const [fOrderId, setFOrderId] = useState<string>('');
    const [fSupplement, setFSupplement] = useState<number | undefined>(undefined);
    const [fSupplier, setFSupplier] = useState<string | undefined>(undefined);
    const [fReference, setFReference] = useState<string | undefined>(undefined);

    // Hilfsfunktion zur Kürzung langer Bezeichnungen
    const truncateDesc = (desc?: string, maxLen = 38): string => {
        if (!desc) return '';
        return desc.length > maxLen ? desc.substring(0, maxLen) + '[…]' : desc;
    };

    // ---- Daten laden (Orders + OrderCards)
    const load = async () => {
        if (!selectedProject || selectedProject === 'Alle') {
            setRows([]);
            setCardMap(new Map());
            setExpandedMap({});
            return;
        }

        setLoading(true);
        try {
            const ordersUrl = `${API_BASE_URL}/getOrdersByProject?auth=VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU&projectNo=${encodeURIComponent(
                selectedProject,
            )}`;
            const cardsUrl = `${API_BASE_URL}/getNetworkPlanOrderCardData?auth=VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU&projectNo=${encodeURIComponent(
                selectedProject,
            )}`;

            const [ordersRes, cardsRes] = await Promise.all([
                axios.get<OrderAPI[]>(ordersUrl, { headers: { 'X-Apikey': X_API_KEY } }),
                axios.get<OrderCardAPI[]>(cardsUrl, { headers: { 'X-Apikey': X_API_KEY } }),
            ]);

            const cm = new Map<string, OrderCardAPI>();
            (cardsRes.data || []).forEach((c) => {
                const key = `${Number(c.supplement)}-${Number(c.protocol)}`;
                cm.set(key, c);
            });
            setCardMap(cm);

            const mapped: Row[] = (ordersRes.data || []).map((o) => ({
                key: String(o.id),
                supplierNo: o.supplierNo ?? null,
                statusName: (o.statusMapping?.statusName ?? o.statusName ?? '') as string,
                statusColor: (o.statusMapping?.statusColor ?? o.statusColor ?? '') as string,
                purchaseOrderIdentifierNo: o.purchaseOrderIdentifierNo ?? '',
                fullSupplierName: o.fullSupplierName ?? '',
                ourReference: o.ourReference ?? '',
                purchaseOrderDate: o.purchaseOrderDate ?? '',
                deliveryDateMax: o.deliveryDateMax ?? '',
                deliveryDateConfirmedMax: o.deliveryDateConfirmedMax ?? '',
                goodsReceivingDate: o.goodsReceivingDate ?? '',
                projectNo: o.projectNo ?? '',
                supplement: o.supplement ?? 0,
                protocol: o.protocol ?? 0,
                weight: o.weight ?? '',
                totalSum: o.totalSum ?? '',
                currency: o.currency ?? '',
            }));

            setRows(mapped);
            // Beim Laden standardmäßig ALLE zugeklappt
            setExpandedMap({});
        } catch (e) {
            console.error(e);
            message.error('Bestellungen oder Auftragskarten konnten nicht geladen werden.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProject]);

    // ---- PDF öffnen
    const openPdf = async (orderNo: string) => {
        try {
            const url = `${API_BASE_URL}/getOrderByID?orderNo=${encodeURIComponent(orderNo)}`;
            const { data } = await axios.get<{ backendMessage: string }>(url, {
                headers: { 'X-Apikey': X_API_KEY },
            });
            const raw = data?.backendMessage || '';
            const base64 = raw.includes('base64,') ? raw.split('base64,').pop()! : raw;
            if (!base64) throw new Error('Kein PDF erhalten');

            const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
            const blob = new Blob([bytes], { type: 'application/pdf' });
            const link = URL.createObjectURL(blob);
            window.open(link, '_blank');
            setTimeout(() => URL.revokeObjectURL(link), 30000);
        } catch (e) {
            console.error(e);
            Modal.error({ title: 'PDF konnte nicht geöffnet werden', content: 'Bitte später erneut versuchen.' });
        }
    };

    // ---- Filteroptionen
    const statusOptions = useMemo(() => {
        const set = new Set(rows.map((r) => r.statusName).filter(Boolean));
        return ['Alle', ...Array.from(set)];
    }, [rows]);

    const supplierOptions = useMemo(() => {
        const set = new Set(rows.map((r) => r.fullSupplierName).filter(Boolean));
        return Array.from(set);
    }, [rows]);

    const refOptions = useMemo(() => {
        const set = new Set(rows.map((r) => r.ourReference).filter(Boolean));
        return Array.from(set);
    }, [rows]);

    const suppOptions = useMemo(() => {
        const set = new Set(rows.map((r) => r.supplement));
        return Array.from(set).sort((a, b) => a - b);
    }, [rows]);

    // ---- Filter anwenden
    const searchedRows = useMemo(() => {
        return rows.filter((r) => {
            if (fStatus && fStatus !== 'Alle' && r.statusName !== fStatus) return false;
            if (fOrderId && !r.purchaseOrderIdentifierNo.toLowerCase().includes(fOrderId.toLowerCase()))
                return false;
            if (fSupplement !== undefined && fSupplement !== null && r.supplement !== fSupplement)
                return false;
            if (fSupplier && r.fullSupplierName !== fSupplier) return false;
            if (fReference && r.ourReference !== fReference) return false;
            return true;
        });
    }, [rows, fStatus, fOrderId, fSupplement, fSupplier, fReference]);

    // ---- Gruppierte Datenstruktur für die Card-Ansicht
    const groupedByCard = useMemo(() => {
        // key = "supp-prot" -> { card, projectNo, supp, prot, items }
        const map = new Map<
            string,
            { card: OrderCardAPI | undefined; projectNo: string; supp: number; prot: number; items: Row[] }
        >();

        for (const r of searchedRows) {
            const key = `${r.supplement}-${r.protocol}`;
            if (!map.has(key)) {
                map.set(key, {
                    card: cardMap.get(key),
                    projectNo: r.projectNo,
                    supp: r.supplement,
                    prot: r.protocol,
                    items: [],
                });
            }
            map.get(key)!.items.push(r);
        }

        let groups = Array.from(map.values());

        // Reihenfolge in Items: externe vor interne (Müssig AG ans Ende)
        groups.forEach((g) =>
            g.items.sort((a, b) => {
                const isIntA = /müssig\s*ag/i.test(a.fullSupplierName || '');
                const isIntB = /müssig\s*ag/i.test(b.fullSupplierName || '');
                if (isIntA !== isIntB) return isIntA ? 1 : -1;
                return a.purchaseOrderIdentifierNo.localeCompare(b.purchaseOrderIdentifierNo);
            }),
        );

        // ---- Checkbox-Filter auf Gruppenebene ---------------------------------
        if (onlyInProduction) {
            groups = groups.filter((g) => {
                const { text } = getAggregatedOrderStatus(g.items);
                return (text || '').toLowerCase().includes('noch in produktion');
            });
        }

        if (onlyNotMounted) {
            groups = groups.filter((g) => !isCardMounted(g.card));
        }

        // ---- Sortierung ---------------------------------------------------------
        if (mountSort !== 'none') {
            groups.sort((a, b) => {
                const ka = mountSortKey(a.card);
                const kb = mountSortKey(b.card);
                if (ka === kb) return 0;
                return mountSort === 'asc' ? ka - kb : kb - ka;
            });
        } else {
            // Default: Nachtrag, dann Protokoll
            groups.sort((a, b) => (a.supp !== b.supp ? a.supp - b.supp : a.prot - b.prot));
        }

        return groups;
        // Dependencies inkl. Filter/Sort
    }, [searchedRows, cardMap, onlyInProduction, onlyNotMounted, mountSort]);

    // ---- Tabellenspalten (ungruppiert)
    const columns: ColumnsType<Row> = [
        {
            title: (
                <div className="oo2-header">
                    <div>Status</div>
                    <Select
                        size="small"
                        className="oo2-header-filter"
                        value={fStatus ?? 'Alle'}
                        onChange={(v) => setFStatus(v === 'Alle' ? undefined : v)}
                        options={statusOptions.map((v) => ({ value: v, label: v }))}
                    />
                </div>
            ),
            key: 'status',
            width: 160,
            render: (_: any, r) => <Badge text={r.statusName} color={r.statusColor} />,
        },
        {
            title: (
                <div className="oo2-header">
                    <div>Bestellnummer</div>
                    <Input
                        size="small"
                        allowClear
                        className="oo2-header-filter"
                        placeholder="Suchen…"
                        value={fOrderId}
                        onChange={(e) => setFOrderId(e.target.value)}
                    />
                </div>
            ),
            dataIndex: 'purchaseOrderIdentifierNo',
            key: 'orderNo',
            width: 170,
            sorter: (a, b) => a.purchaseOrderIdentifierNo.localeCompare(b.purchaseOrderIdentifierNo),
            className: 'oo2-cell--order',
        },
        {
            title: (
                <div className="oo2-header">
                    <div>Projekt / Nachtrag / Protokoll</div>
                    <Select
                        size="small"
                        className="oo2-header-filter"
                        placeholder="Nachtrag"
                        allowClear
                        value={fSupplement}
                        onChange={(v) => setFSupplement(v)}
                        options={suppOptions.map((n) => ({ value: n, label: pad2(n) }))}
                    />
                </div>
            ),
            key: 'projectSp',
            width: 200,
            render: (_: any, r) => composeProjectSp(r.projectNo, r.supplement, r.protocol),
            sorter: (a, b) => {
                const aa = `${a.projectNo}-${a.supplement}-${a.protocol}`;
                const bb = `${b.projectNo}-${b.supplement}-${b.protocol}`;
                return aa.localeCompare(bb);
            },
        },
        {
            title: (
                <div className="oo2-header">
                    <div>Lieferant</div>
                    <Select
                        size="small"
                        className="oo2-header-filter"
                        allowClear
                        placeholder="Filtern…"
                        value={fSupplier}
                        onChange={(v) => setFSupplier(v)}
                        options={supplierOptions.map((s) => ({ value: s, label: s }))}
                    />
                </div>
            ),
            dataIndex: 'fullSupplierName',
            key: 'supplier',
            width: 260,
            sorter: (a, b) => a.fullSupplierName.localeCompare(b.fullSupplierName),
        },
        {
            title: (
                <div className="oo2-header">
                    <div>Besteller</div>
                    <Select
                        size="small"
                        className="oo2-header-filter"
                        allowClear
                        placeholder="Filtern…"
                        value={fReference}
                        onChange={(v) => setFReference(v)}
                        options={refOptions.map((x) => ({ value: x, label: x }))}
                    />
                </div>
            ),
            dataIndex: 'ourReference',
            key: 'ourReference',
            width: 190,
            sorter: (a, b) => a.ourReference.localeCompare(b.ourReference),
        },
        {
            title: 'Bestelldatum',
            dataIndex: 'purchaseOrderDate',
            key: 'purchaseOrderDate',
            width: 130,
            sorter: (a, b) => (a.purchaseOrderDate || '').localeCompare(b.purchaseOrderDate || ''),
        },
        { title: 'LT gewünscht', dataIndex: 'deliveryDateMax', key: 'deliveryDateMax', width: 130 },
        {
            title: 'Liefertermin bestätigt',
            dataIndex: 'deliveryDateConfirmedMax',
            key: 'deliveryDateConfirmedMax',
            width: 170,
        },
        { title: 'Wareneingang', dataIndex: 'goodsReceivingDate', key: 'goodsReceivingDate', width: 130 },
        {
            title: 'Gewicht',
            dataIndex: 'weight',
            key: 'weight',
            width: 120,
            render: (v: string) => <div className="align-right">{v ? `${fmt2(v)} kg` : '—'}</div>,
            className: 'align-right',
        },
        {
            title: 'AB-Betrag',
            key: 'total',
            width: 140,
            render: (_: any, r) => (
                <div className="align-right">
                    {r.totalSum ? `${fmt2(r.totalSum)} ${r.currency || ''}`.trim() : '—'}
                </div>
            ),
            sorter: (a, b) => parseFloat(a.totalSum || '0') - parseFloat(b.totalSum || '0'),
            className: 'align-right',
        },
        {
            title: '',
            key: 'pdf',
            fixed: 'right',
            width: 56,
            render: (_: any, r) => {
                const orderNo = r.purchaseOrderIdentifierNo?.split('/')[0]?.trim() || '';
                return (
                    <Tooltip title="Bestellung als PDF öffnen">
                        <Button size="small" icon={<FilePdfOutlined />} onClick={() => openPdf(orderNo)} />
                    </Tooltip>
                );
            },
        },
    ];

    // ---- Expand/Collapse helpers ---------------------------------------------
    const isExpanded = (k: string) => !!expandedMap[k];
    const toggleOne = (k: string) => setExpandedMap((m) => ({ ...m, [k]: !m[k] }));
    const setAll = (value: boolean) => {
        const next: Record<string, boolean> = {};
        for (const g of groupedByCard) {
            const k = `${g.supp}-${g.prot}`;
            next[k] = value;
        }
        setExpandedMap(next);
    };

    // -----------------------------------------------------------------------------

    return (
        <div className="oo2-wrap">
            <div className="oo2-toolbar">
                <Space wrap>
                    <Button icon={<ReloadOutlined/>} onClick={load} loading={loading}>
                        Daten neu laden
                    </Button>

                    {grouped && (
                        <>
                            <Button icon={<PlusSquareOutlined/>} onClick={() => setAll(true)}/>
                            <Button icon={<MinusSquareOutlined/>} onClick={() => setAll(false)}/>
                        </>
                    )}

                    {/* ——— Filter ——— */}
                    <Checkbox
                        checked={onlyInProduction}
                        onChange={(e) => setOnlyInProduction(e.target.checked)}
                    >
                        Nur in Produktion
                    </Checkbox>
                    <Checkbox
                        checked={onlyNotMounted}
                        onChange={(e) => setOnlyNotMounted(e.target.checked)}
                    >
                        Nicht montiert
                    </Checkbox>

                    {/* ——— Sortierung Montagedatum ——— */}
                    <Select
                        size="small"
                        value={mountSort}
                        onChange={(v: MountSort) => setMountSort(v)}
                        style={{width: 200}}
                        options={[
                            {value: 'none', label: 'Sortierung: Montagedatum —'},
                            {value: 'asc', label: 'Montagedatum ↑ (früh zuerst)'},
                            {value: 'desc', label: 'Montagedatum ↓ (spät zuerst)'},
                        ]}
                    />
                </Space>
            </div>


            <div className="oo2-data-area">
                <Spin spinning={loading} tip="Laden...">
                    {!selectedProject || selectedProject === 'Alle' ? (
                        <Empty description="Bitte ein Projekt auswählen."/>
                    ) : grouped ? (
                        <div className="oo2-grouped-list">
                            {groupedByCard.map(({card, projectNo, supp, prot, items}) => {
                                const k = `${supp}-${prot}`;
                                const expanded = isExpanded(k);
                                const orderCount = items.length;
                                const {text: orderStateText, color: orderStateColor} = getAggregatedOrderStatus(items);
                                const latestDelivery = getLatestDeliveryText(items);
                                const cardStatusColored = getOrderCardStatusColored(card);

                                return (
                                    <Card
                                        key={k}
                                        className="oo2-card-parent"
                                        bodyStyle={{
                                            padding: expanded ? 12 : 0,
                                            height: expanded ? 'auto' : 0,
                                            overflow: 'hidden',
                                        }}
                                        headStyle={{padding: '6px 12px'}}
                                        title={
                                            <div className="oo2-card-parent__title">
                                                {/* LINKS: kompakte, ausgerichtete Spalten -> Nummer/Bezeichnung | Badge | Termin */}
                                                <div className="oo2-card-parent__title">
                                                    {/* LINKS: Nummer/Bezeichnung | Badge | Termin – sauber ausgerichtet */}
                                                    <div className="title-left-grid">
                                                        <div
                                                            className="spdesc"
                                                            title={`${composeProjectSp(projectNo, supp, prot)} ${card?.description ?? ''}`}
                                                        >
                                                            <span
                                                                className="sp">{composeProjectSp(projectNo, supp, prot)}</span>
                                                            {card?.description && (
                                                                <span
                                                                    className="desc">{truncateDesc(card.description)}</span>
                                                            )}
                                                        </div>
                                                        <div className="tl-status-only">
                                                            <Badge text={cardStatusColored.text}
                                                                   color={cardStatusColored.color}/>
                                                        </div>
                                                        <div className="tl-mount-only">
                                                            <CalendarOutlined style={{fontSize: 12, marginRight: 6}}/>
                                                            {card?.mountingDate ?? (card as any)?.MountingDate ?? '—'}
                                                        </div>
                                                    </div>
                                                </div>
                                                {/* RECHTS: spätestes Lieferdatum | Anzahl | Bestell-Status (nur Badge) | Toggle */}
                                                <div className="title-right">
                                                    <div className="meta-block">
                                                        <span className="meta-label">Spätestes Lieferdatum</span>
                                                        <span className="meta-value">{latestDelivery}</span>
                                                    </div>
                                                    <div className="meta-block">
                                                        <span className="meta-label">Bestellungen</span>
                                                        <span className="meta-value">{orderCount}</span>
                                                    </div>
                                                    <div className="meta-block no-label">
                                                        <Badge text={orderStateText} color={orderStateColor}/>
                                                    </div>
                                                    <div className="meta-block">
                                                        <Button
                                                            size="small"
                                                            icon={expanded ? <MinusSquareOutlined/> :
                                                                <PlusSquareOutlined/>}
                                                            onClick={() => toggleOne(k)}
                                                        >
                                                            {expanded ? '' : ''}
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        }
                                    >
                                        {expanded && (
                                            <div className="oo2-child-grid">
                                                <div className="oo2-child-grid__head">
                                                    <div>Status</div>
                                                    <div>Bestellnummer</div>
                                                    <div>Lieferant</div>
                                                    <div>Besteller</div>
                                                    <div>Bestelldatum</div>
                                                    <div>LT gewünscht</div>
                                                    <div>Liefertermin bestätigt</div>
                                                    <div>Wareneingang</div>
                                                    <div className="align-right">Gewicht</div>
                                                    <div className="align-right">AB-Betrag</div>
                                                    <div></div>
                                                </div>
                                                {items.map((r) => {
                                                    const orderNo = r.purchaseOrderIdentifierNo?.split('/')[0]?.trim() || '';
                                                    return (
                                                        <div key={r.key} className="oo2-child-grid__row">
                                                            <div>
                                                                <Badge text={r.statusName} color={r.statusColor}/>
                                                            </div>
                                                            <div
                                                                className="bold">{r.purchaseOrderIdentifierNo || '—'}</div>
                                                            <div>{r.fullSupplierName || '—'}</div>
                                                            <div>{r.ourReference || '—'}</div>
                                                            <div>{r.purchaseOrderDate || '—'}</div>
                                                            <div>{r.deliveryDateMax || '—'}</div>
                                                            <div>{r.deliveryDateConfirmedMax || '—'}</div>
                                                            <div>{r.goodsReceivingDate || '—'}</div>
                                                            <div className="align-right">
                                                                {r.weight ? `${fmt2(r.weight)} kg` : '—'}
                                                            </div>
                                                            <div className="align-right">
                                                                {r.totalSum
                                                                    ? `${fmt2(r.totalSum)} ${r.currency || ''}`.trim()
                                                                    : '—'}
                                                            </div>
                                                            <div className="row-actions">
                                                                <Tooltip title="Bestellung als PDF öffnen">
                                                                    <Button
                                                                        size="small"
                                                                        icon={<FilePdfOutlined/>}
                                                                        onClick={() => openPdf(orderNo)}
                                                                    />
                                                                </Tooltip>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </Card>
                                );
                            })}

                            {!groupedByCard.length && (
                                <Empty description="Keine Treffer für die gesetzten Filter."
                                       className="oo2-empty-inside"/>
                            )}
                        </div>
                    ) : (
                        <Table<Row>
                            columns={columns}
                            dataSource={searchedRows}
                            loading={loading}
                            rowKey="key"
                            pagination={{pageSize: 100}}
                            sticky
                            scroll={{y: 600, x: 1500}}
                        />
                    )}
                </Spin>
            </div>
        </div>
    );
};

export default OrderOverviewConfiguration;